/**
 * Typed fetch helper for the Payments Sandbox endpoints.
 *
 * The six endpoints under `/api/v1/admin/payments/sandbox` are not yet in the
 * generated OpenAPI `paths`, so the singleton `api` client (`@favelafood/shared/api/client`)
 * cannot type them without an unsafe cast. This tiny helper REUSES the same
 * primitives the rest of the panel relies on, so behaviour matches everywhere:
 *
 *   - `baseUrl` from `VITE_API_BASE` (same default as `client.ts`).
 *   - `Authorization: Bearer <token>` via `getAccessToken()` (same header).
 *   - On `!res.ok`, parse the JSON envelope and throw `ApiError(status, payload,
 *     'HTTP <status>')` from `@favelafood/shared/api/errors`, so SANDBOX_DISABLED (403) and
 *     SANDBOX_REAL_PROVIDER (422) surface via `error.status` + `error.code`
 *     exactly like every other feature, and TanStack Query's `error` channel
 *     carries them.
 *
 * NOTE (cf. `features/error-logs/hooks.ts`, which documents a similar
 * "param not in the generated spec" workaround): this helper deliberately does
 * NOT replicate the 401-refresh / 429-backoff retry logic of
 * `authRetryMiddleware`. For admin sandbox (dev / diagnostic) tooling that is
 * acceptable. If retry is needed later, the cleanest upgrade is to regenerate
 * `paths` and switch these calls to `api.GET` / `api.POST`.
 */
import { getAccessToken } from '@favelafood/shared/auth/tokens';
import { ApiError, type ApiErrorPayload } from '@favelafood/shared/api/errors';

/** Base path shared by every sandbox endpoint. */
export const SANDBOX_BASE = '/api/v1/admin/payments/sandbox';

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

/** Query param values we serialize; `null`/`undefined` entries are skipped. */
export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

/** Builds a query string from a params object, skipping null/undefined. */
const toQueryString = (query: QueryParams | undefined): string => {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const authHeaders = (): HeadersInit => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

/**
 * Best-effort decode of an error body — Spring always returns JSON for errors,
 * but proxies can inject HTML pages that fail JSON parsing (mirrors `client.ts`).
 */
const parseErrorPayload = async (res: Response): Promise<ApiErrorPayload> => {
  try {
    const body = (await res.clone().json()) as unknown;
    if (typeof body === 'object' && body !== null) {
      return body as ApiErrorPayload;
    }
  } catch {
    /* fall through to statusText */
  }
  return { message: res.statusText };
};

const ensureOk = async (res: Response): Promise<void> => {
  if (res.ok) return;
  const payload = await parseErrorPayload(res);
  throw new ApiError(res.status, payload, `HTTP ${res.status}`);
};

/** GET a sandbox endpoint and parse the JSON body as `T`. */
export async function sandboxGet<T>(path: string, query?: QueryParams): Promise<T> {
  const res = await fetch(`${baseUrl}${SANDBOX_BASE}${path}${toQueryString(query)}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  await ensureOk(res);
  return (await res.json()) as T;
}

/** POST a JSON `body` to a sandbox endpoint and parse the JSON response as `T`. */
export async function sandboxPost<T, B>(path: string, body: B): Promise<T> {
  const res = await fetch(`${baseUrl}${SANDBOX_BASE}${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return (await res.json()) as T;
}

/** Base path for admin endpoints that live OUTSIDE `/payments/sandbox`. */
export const ADMIN_BASE = '/api/v1/admin';

/**
 * GET an admin endpoint that is NOT under `/payments/sandbox` (e.g. the Banco
 * Inter health-check, `GET /api/v1/admin/inter/health-check`, EP-6). Sibling of
 * `sandboxGet` — reuses the same `authHeaders()` + `ensureOk()` + `ApiError`
 * primitives, so the same `error.status` / `error.code` contract holds. `path`
 * is appended to `ADMIN_BASE` (e.g. `'/inter/health-check'`).
 */
export async function adminGet<T>(path: string, query?: QueryParams): Promise<T> {
  const res = await fetch(`${baseUrl}${ADMIN_BASE}${path}${toQueryString(query)}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  await ensureOk(res);
  return (await res.json()) as T;
}
